import React, { useEffect, useRef, useState } from 'react';
import './storefront-loading.css';

const reload = () => window.location.reload();

/** No invented progress: the parent supplies actual catalogue/design readiness. */
export function StorefrontLoadingSplash({ error = '', onRetry = reload }) {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setSlow(true), 12000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="sf-loading" data-testid="storefront-loading">
      <div className="sf-loading-content">
        <div className="sf-loading-dish" aria-hidden="true">
          <span className="sf-loading-steam sf-loading-steam-one" />
          <span className="sf-loading-steam sf-loading-steam-two" />
          <span className="sf-loading-steam sf-loading-steam-three" />
          <span className="sf-loading-leaf" />
          <span className="sf-loading-bowl" />
          <span className="sf-loading-plate" />
        </div>
        <div role={error ? 'alert' : 'status'} aria-live="polite" aria-atomic="true">
          <p className="sf-loading-eyebrow">{error ? 'A little interruption' : 'Good food is worth a moment'}</p>
          <h1>{error ? 'Let’s try that again.' : 'Something yummy is loading.'}</h1>
          <p className="sf-loading-description">{error
            ? 'We couldn’t load the storefront. Please try again.'
            : slow ? 'This is taking longer than usual. You can wait a little longer or try again.'
              : 'Getting the menu and all the delicious details ready for you.'}</p>
        </div>
        {error || slow
          ? <button type="button" className="sf-loading-retry" onClick={onRetry}>Try again</button>
          : <div className="sf-loading-dots" aria-hidden="true"><i /><i /><i /></div>}
      </div>
    </div>
  );
}

/**
 * Reveal once per storefront visit. Mount the finished design under the splash
 * so the browser can fetch its actual responsive images and fonts. Only wait
 * for images in the first viewport; below-fold lazy images/video cannot block
 * shopping. Broken/slow decorative assets get a bounded grace period.
 */
export default function StorefrontEntrance({ ready, error, onReady, children }) {
  const [revealed, setRevealed] = useState(false);
  const content = useRef(null);
  useEffect(() => {
    if (!ready || error || revealed || !content.current) return undefined;
    let disposed = false;
    const cleanups = [];
    const images = Array.from(content.current.querySelectorAll('img')).filter((img) => {
      const rect = img.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
    });
    const pending = images.map((img) => new Promise((resolve) => {
      if (img.complete) { resolve(); return; }
      const done = () => resolve();
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
      cleanups.push(() => { img.removeEventListener('load', done); img.removeEventListener('error', done); });
    }));
    if (document.fonts?.ready) pending.push(document.fonts.ready.catch(() => {}));
    const reveal = () => {
      if (disposed) return;
      setRevealed(true);
      onReady?.();
    };
    const timer = setTimeout(reveal, 2500);
    Promise.all(pending).then(reveal);
    return () => { disposed = true; clearTimeout(timer); cleanups.forEach((fn) => fn()); };
  }, [ready, error, revealed, onReady]);

  const waiting = !revealed || !!error;
  return (
    <div className={waiting ? 'sf-entrance sf-entrance-pending' : 'sf-entrance'}>
      {waiting && <StorefrontLoadingSplash error={error} />}
      {(ready || revealed) && !error && <div ref={content} className="sf-entrance-content" aria-busy={waiting} inert={waiting} aria-hidden={waiting || undefined} style={waiting ? { visibility: 'hidden' } : undefined}>{children}</div>}
    </div>
  );
}
